import React, { useMemo, useState } from "react";
import axiosInstance from "../../config/axiosInstance";
import { CRM } from "../../config/apiConfig";
import {
  dateToHHMM,
  fileIconFor,
  fmtBR,
  labelIcon,
  prettySize,
} from "../../utils/utils";
import {
  MdEdit,
  MdDeleteOutline,
  MdCheckCircle,
  MdReplay,
} from "react-icons/md";
import moment from "moment";

function TaskItem({
  t,
  isDone,
  labelMap,
  getUserNameById,
  startEditTask,
  handleToggleTaskStatus,
  handleDeleteTask,
  onConfirmComplete,
}) {
  const [attLoading, setAttLoading] = useState({});

  const isOverdue =
    !isDone && t?.dueDate && moment(t.dueDate).isBefore(moment());

  let cardColorClasses = "bg-white border-gray-200";
  if (isDone) {
    cardColorClasses = "bg-green-50 border-green-200";
  } else if (isOverdue) {
    cardColorClasses = "bg-red-50 border-red-200";
  }

  const responsibleNames = useMemo(() => {
    const names = [];
    const arrayCandidates =
      t?.responsibles ||
      t?.responsibleUsers ||
      t?.taskResponsibles ||
      t?.TaskResponsibles;
    if (Array.isArray(arrayCandidates)) {
      arrayCandidates.forEach((r) => {
        const rid = r?.id || r?.userId || r?.user_id || r?.UserId;
        const nm =
          r?.name ||
          r?.user?.name ||
          r?.User?.name ||
          (rid ? getUserNameById?.(rid) : "");
        if (nm) names.push(nm);
      });
    }
    if (t?.responsible?.name) names.push(t.responsible.name);
    else if (t?.responsibleId) {
      const nm = getUserNameById?.(t.responsibleId);
      if (nm) names.push(nm);
    }
    return Array.from(new Set(names.filter(Boolean)));
  }, [t, getUserNameById]);

  async function openAttachment(att) {
    const key = att.id || att.objectName || att.gcsKey;
    setAttLoading((s) => ({ ...s, [key]: true }));
    try {
      const alreadyPublic =
        att.url || att.publicUrl || att.signedUrl || att.downloadUrl;
      if (alreadyPublic) {
        window.open(alreadyPublic, "_blank", "noopener,noreferrer");
        return;
      }
      const res = await axiosInstance.get(
        CRM.TASK_ATTACHMENT_URL(
          t.cardId || t.card_id || t.CardId,
          t.id,
          att.id
        ),
        { params: { ttl: 5 * 60 * 1000 } }
      );
      const url = res?.data?.data?.url;
      if (!url) throw new Error("URL não retornada");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("openAttachment:", e);
      alert("Não foi possível abrir o anexo.");
    } finally {
      setAttLoading((s) => {
        const copy = { ...s };
        delete copy[key];
        return copy;
      });
    }
  }

  return (
    <div
      className={`rounded-md border p-2 transition-colors relative ${cardColorClasses}`}
    >
      {isDone && (
        <span className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 border border-green-200 text-green-800">
          ✅ Concluída
        </span>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12px] flex items-center gap-2">
            {labelIcon(t.type)}{" "}
            <span className="font-semibold">{labelMap[t.type] || "Ação"}</span>
          </div>

          {t.note && (
            <div
              className="rich-text text-[13px] mt-1 text-gray-800"
              dangerouslySetInnerHTML={{ __html: t.note || "" }}
            />
          )}

          {Array.isArray(t.attachments) && t.attachments.length > 0 && (
            <div className="mt-2 mb-2 text-[12px]">
              <div className="text-gray-600 font-medium mb-1">Anexos</div>
              <ul className="space-y-1">
                {t.attachments.map((a) => {
                  const name =
                    a.originalName ||
                    a.filename ||
                    a.name ||
                    a.objectName ||
                    a.gcsKey ||
                    "arquivo";
                  const size = a.size ? ` • ${prettySize(a.size)}` : "";
                  const icon = fileIconFor(
                    a.mimeType || a.mimetype || "",
                    name
                  );
                  const key = a.id || a.objectName || a.gcsKey || name;
                  const loading = attLoading[key];

                  return (
                    <li key={key} className="flex items-center gap-2">
                      <span className="shrink-0">{icon}</span>
                      <button
                        type="button"
                        className={`underline text-blue-600 hover:no-underline disabled:text-gray-400 disabled:no-underline ${
                          loading ? "cursor-wait" : ""
                        }`}
                        onClick={() => openAttachment(a)}
                        disabled={!!loading}
                        title={name}
                      >
                        {name}
                      </button>
                      <span className="text-gray-400">{size}</span>
                      {loading && (
                        <span className="text-gray-400"> • gerando link…</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* METAS DESTACADAS: RESPONSÁVEL + PRAZO */}
          <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
              <span className="font-semibold">Atribuído para:</span>
              <span>
                {responsibleNames.length
                  ? responsibleNames.join(", ")
                  : "Nenhum responsável"}
              </span>
            </div>

            <div
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-slate-700 ${
                isOverdue
                  ? "bg-red-100 border-red-300 text-red-800"
                  : "bg-slate-50 border-slate-200"
              }`}
            >
              <span className="font-semibold">Prazo:</span>
              {t?.dueDate ? (
                <span>
                  {moment(t.dueDate).format("DD/MM/yyyy")} •{" "}
                  {dateToHHMM(t.dueDate)}h
                </span>
              ) : (
                <span>Sem prazo</span>
              )}
            </div>
          </div>

          {/* METAS SECUNDÁRIAS: criado por / em */}
          <div className="text-[11px] text-gray-600 mt-1 flex flex-wrap gap-2">
            <span>
              Criado por: <b>{t?.creator?.name}</b>
            </span>
            <span>
              Criado em: <b>{moment(t?.createdAt).format("DD/MM/yyyy")}</b>
            </span>
          </div>

          {isDone && t.completedAt && (
            <div className="mt-1 text-[11px] text-green-800">
              Concluída em <b>{fmtBR(t.completedAt)}</b>
              {t?.completedAt && dateToHHMM(t.completedAt) ? (
                <span> às <b>{dateToHHMM(t.completedAt)}h</b></span>
              ) : null}
              {t?.completer?.name ? (
                <span> por <b>{t.completer.name}</b></span>
              ) : null}
            </div>
          )}
        </div>

        {/* AÇÕES INLINE */}
        <div className="shrink-0 flex items-center gap-1">
          <button
            type="button"
            className="p-1.5 rounded-md border text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => startEditTask(t)}
            aria-label="Editar tarefa"
            title="Editar"
          >
            <MdEdit className="w-4 h-4" />
          </button>

          <button
            type="button"
            className="p-1.5 rounded-md border text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => {
              if (t.status === "done") {
                handleToggleTaskStatus(t, false);
              } else {
                onConfirmComplete?.(t);
              }
            }}
            aria-label={
              t.status === "done" ? "Reabrir tarefa" : "Concluir tarefa"
            }
            title={t.status === "done" ? "Reabrir" : "Concluir"}
          >
            {t.status === "done" ? (
              <MdReplay className="w-4 h-4" />
            ) : (
              <MdCheckCircle className="w-4 h-4" />
            )}
          </button>

          <button
            type="button"
            className="p-1.5 rounded-md border text-red-600 bg-white hover:bg-red-50"
            onClick={() => handleDeleteTask(t)}
            aria-label="Excluir tarefa"
            title="Excluir"
          >
            <MdDeleteOutline className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(TaskItem);
